/**
 * OpsProjectDetailPage — full project page with:
 * - Header (code, client, status, progress)
 * - Members panel
 * - Tasks list (create/assign/update)
 * - Status + notes editing
 * Route: /ops/projects/:id
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { opsApi, useOpsAuth, ROLE_LABELS_AR, isExecutiveDirector, formatAuthError } from './OpsAuthContext';
import OpsLayout from './OpsLayout';
import {
  PROJECT_STATUS,
  PROJECT_STATUS_ORDER,
  TASK_STATUS,
  TASK_STATUS_ORDER,
  fmtDate,
} from './opsConstants';

const OpsProjectDetailPage = () => {
  const { id } = useParams();
  const { user } = useOpsAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [banner, setBanner] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await opsApi.get(`/projects/${id}`);
      setProject(data.project);
      setTasks(data.tasks);
      setCanEdit(data.can_edit);
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
    opsApi.get('/users').then((r) => setTeam(r.data)).catch(() => {});
  }, [refresh]);

  const flash = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(''), 2500);
  };

  const updateStatus = async (patch) => {
    try {
      const { data } = await opsApi.patch(`/projects/${id}/status`, patch);
      setProject(data);
      flash('تم الحفظ');
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const requestApproval = async () => {
    const note = window.prompt('اكتبي ملاحظة اختيارية للمدير التنفيذي (تُرفق مع طلب الاعتماد):', '');
    if (note === null) return;
    try {
      const { data } = await opsApi.post(`/projects/${id}/request-approval`, { note });
      setProject(data);
      flash('تم إرسال طلب الاعتماد إلى المدير التنفيذي ✅');
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const decideOnApproval = async (approve) => {
    const note = window.prompt(approve ? 'ملاحظة الاعتماد (اختياري):' : 'سبب الرفض / التعديلات المطلوبة:', '');
    if (note === null) return;
    if (!approve && !note.trim()) {
      setErr('يجب ذكر سبب الرفض');
      return;
    }
    try {
      const url = approve
        ? `/projects/${id}/approve`
        : `/projects/${id}/reject-approval`;
      const { data } = await opsApi.post(url, { note });
      setProject(data);
      flash(approve ? 'تم اعتماد التسليم ✅' : 'تمت إعادة المشروع لقائد المسار');
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  if (loading) {
    return (
      <OpsLayout title="…" subtitle="Project">
        <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>
      </OpsLayout>
    );
  }

  if (err || !project) {
    return (
      <OpsLayout title="خطأ" subtitle="Project">
        <p className="text-[13px] text-[#B84A3A] font-['Noto_Kufi_Arabic']">{err || 'لا يوجد مشروع'}</p>
        <Link to="/ops/projects" className="text-[13px] text-[#4A5D23] font-['Noto_Kufi_Arabic'] underline mt-4 inline-block">
          العودة إلى المشاريع
        </Link>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout title={project.code} subtitle={`${project.client_name || ''} · ${project.client_org || ''}`}>
      {banner && (
        <div className="mb-3 bg-[#E3EDDE] border border-[#B8CBA2] text-[#2C3814] rounded-xl px-4 py-2 text-[12px] font-['Noto_Kufi_Arabic']">
          {banner}
        </div>
      )}

      {/* Header card */}
      <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6 mb-5">
        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-2">
            <p className="text-[11px] tracking-widest text-[#4A5D23] font-['Noto_Kufi_Arabic'] uppercase font-semibold">
              {project.service_type}
            </p>
            <h2 className="font-['Noto_Kufi_Arabic'] text-[22px] font-bold text-[#1F3D2B] mt-1">
              {project.client_name}
            </h2>
            <p className="font-['Noto_Kufi_Arabic'] text-[13px] text-[#5C6D49] mt-1">
              {project.client_org || 'بدون جهة'} · {project.client_email || '—'} · {project.client_phone || '—'}
            </p>
          </div>
          <div className="text-end md:text-start">
            <p className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">الحالة</p>
            {canEdit ? (
              <select
                data-testid="project-status-select"
                value={project.status}
                onChange={(e) => updateStatus({ status: e.target.value })}
                className="mt-1 text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
              >
                {PROJECT_STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{PROJECT_STATUS[s].label}</option>
                ))}
              </select>
            ) : (
              <span className={`inline-block mt-1 text-[11px] font-['Noto_Kufi_Arabic'] font-semibold px-3 py-1 rounded-full ${PROJECT_STATUS[project.status]?.color}`}>
                {PROJECT_STATUS[project.status]?.label}
              </span>
            )}
          </div>
          <div className="text-end md:text-start">
            <p className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">نسبة الإنجاز</p>
            {canEdit ? (
              <input
                type="number"
                min={0}
                max={100}
                value={project.progress || 0}
                data-testid="project-progress-input"
                onChange={(e) => setProject((p) => ({ ...p, progress: Number(e.target.value) }))}
                onBlur={(e) => updateStatus({ progress: Number(e.target.value) })}
                className="mt-1 w-24 text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
              />
            ) : (
              <p className="mt-1 font-['Playfair_Display'] text-2xl font-semibold text-[#1F3D2B]">
                {project.progress || 0}%
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Delivery approval workflow bar */}
      <ApprovalActionBar
        project={project}
        canEdit={canEdit}
        isDirector={isExecutiveDirector(user)}
        onRequest={requestApproval}
        onDecide={decideOnApproval}
      />

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-5">
        {/* Tasks */}
        <TasksPanel
          projectId={project.id}
          canManage={canEdit}
          tasks={tasks}
          team={team}
          project={project}
          onChanged={refresh}
        />

        {/* Members + notes */}
        <div className="space-y-5">
          <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-5">
            <h3 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B] mb-3">
              فريق المشروع
            </h3>
            <ul className="space-y-2">
              <TeamRow email={project.lead_email} team={team} role="lead" />
              {(project.member_emails || []).map((e) => (
                <TeamRow key={e} email={e} team={team} role="member" />
              ))}
            </ul>
          </section>

          <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-5">
            <h3 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B] mb-2">
              ملاحظات المشروع
            </h3>
            {canEdit ? (
              <textarea
                data-testid="project-notes"
                defaultValue={project.notes || ''}
                onBlur={(e) => updateStatus({ notes: e.target.value })}
                className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 min-h-[100px] bg-white"
                placeholder="أضيفي ملاحظات، مواعيد، قرارات هامة…"
              />
            ) : (
              <p className="text-[13px] font-['Noto_Kufi_Arabic'] text-[#3D512D] whitespace-pre-wrap leading-8">
                {project.notes || '—'}
              </p>
            )}
          </section>
        </div>
      </div>
    </OpsLayout>
  );
};

const TeamRow = ({ email, team, role }) => {
  const u = team.find((t) => t.email === email);
  return (
    <li className="flex items-center justify-between text-[13px] font-['Noto_Kufi_Arabic'] border-b border-[#EFEAE1] pb-2 last:border-0">
      <div>
        <p className="font-semibold text-[#1F3D2B]">{u ? u.name_ar : email}</p>
        {u && <p className="text-[11px] text-[#7A866E]">{u.title_ar || ROLE_LABELS_AR[u.role]}</p>}
      </div>
      {role === 'lead' && (
        <span className="text-[9.5px] tracking-wider uppercase text-[#4A5D23] bg-[#F3F1E9] px-2 py-0.5 rounded-full">
          قائد
        </span>
      )}
    </li>
  );
};

// ------ Tasks Panel ------
const TasksPanel = ({ projectId, canManage, tasks, team, project, onChanged }) => {
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const { user } = useOpsAuth();

  const memberCandidates = useMemo(() => {
    const emails = [project.lead_email, ...(project.member_emails || [])];
    return team.filter((t) => emails.includes(t.email));
  }, [team, project]);

  const create = async () => {
    setErr('');
    if (!title || !assignee) {
      setErr('العنوان والمكلَّف مطلوبان');
      return;
    }
    setBusy(true);
    try {
      await opsApi.post(`/projects/${projectId}/tasks`, {
        title,
        assignee_email: assignee,
      });
      setTitle('');
      setAssignee('');
      setCreating(false);
      onChanged();
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await opsApi.patch(`/projects/${projectId}/tasks/${taskId}`, { status });
      onChanged();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  const removeTask = async (taskId) => {
    try {
      await opsApi.delete(`/projects/${projectId}/tasks/${taskId}`);
      onChanged();
    } catch (e) {
      setErr(formatAuthError(e));
    }
  };

  return (
    <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B]">
          المهام ({tasks.length})
        </h3>
        {canManage && (
          <button
            data-testid="new-task-btn"
            onClick={() => setCreating((c) => !c)}
            className="text-[12px] font-['Noto_Kufi_Arabic'] text-[#4A5D23] border border-[#4A5D23] px-3 py-1.5 rounded-lg hover:bg-[#4A5D23] hover:text-white transition"
          >
            {creating ? 'إغلاق' : '+ مهمة جديدة'}
          </button>
        )}
      </div>

      {creating && canManage && (
        <div className="bg-[#FAFAF7] rounded-xl p-3 mb-4 space-y-2">
          <input
            data-testid="task-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="عنوان المهمة…"
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
          />
          <select
            data-testid="task-assignee-select"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
          >
            <option value="">اختاري المكلَّف…</option>
            {memberCandidates.map((u) => (
              <option key={u.email} value={u.email}>{u.name_ar}</option>
            ))}
          </select>
          {err && <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic']">{err}</p>}
          <div className="flex gap-2">
            <button
              data-testid="task-create-submit"
              onClick={create}
              disabled={busy}
              className="text-[12px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-4 py-2 rounded-lg hover:bg-[#163020] disabled:opacity-50"
            >
              {busy ? '…' : 'إسناد المهمة'}
            </button>
          </div>
        </div>
      )}

      {tasks.length === 0 && (
        <p className="text-[13px] font-['Noto_Kufi_Arabic'] text-[#7A866E] text-center py-6">
          لا توجد مهام بعد
        </p>
      )}

      <ul className="space-y-2">
        {tasks.map((t) => {
          const isMine = t.assignee_email === user.email;
          const u = team.find((m) => m.email === t.assignee_email);
          return (
            <li
              key={t.id}
              data-testid={`task-item-${t.id}`}
              className="border border-[#EFEAE1] rounded-xl p-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-['Noto_Kufi_Arabic'] text-[14px] font-semibold text-[#1F3D2B]">
                  {t.title}
                </p>
                {t.description && (
                  <p className="font-['Noto_Kufi_Arabic'] text-[12px] text-[#5C6D49] mt-1">
                    {t.description}
                  </p>
                )}
                <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-1">
                  المكلَّف: {u ? u.name_ar : t.assignee_email} · أُنشئت {fmtDate(t.created_at)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {(canManage || isMine) ? (
                  <select
                    data-testid={`task-status-${t.id}`}
                    value={t.status}
                    onChange={(e) => updateTaskStatus(t.id, e.target.value)}
                    className={`text-[11px] font-['Noto_Kufi_Arabic'] rounded-full px-3 py-1 border-0 ${TASK_STATUS[t.status]?.color || ''}`}
                  >
                    {TASK_STATUS_ORDER.map((s) => (
                      <option key={s} value={s}>{TASK_STATUS[s].label}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`text-[10px] font-['Noto_Kufi_Arabic'] font-semibold px-2 py-0.5 rounded-full ${TASK_STATUS[t.status]?.color}`}>
                    {TASK_STATUS[t.status]?.label}
                  </span>
                )}
                {canManage && (
                  <button
                    data-testid={`task-delete-${t.id}`}
                    onClick={() => removeTask(t.id)}
                    className="text-[10px] font-['Noto_Kufi_Arabic'] text-[#7A4B44] hover:underline"
                  >
                    حذف
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default OpsProjectDetailPage;

// ------ Approval Action Bar ------
const ApprovalActionBar = ({ project, canEdit, isDirector, onRequest, onDecide }) => {
  const status = project.status;
  const isPending = status === 'pending_approval';
  const isFinal = ['delivered', 'closed', 'cancelled'].includes(status);

  if (isFinal) {
    return (
      <section
        data-testid="approval-final-bar"
        className="mb-5 bg-[#C6DFCF] border border-[#96BFA5] rounded-2xl px-5 py-3 flex items-center justify-between"
      >
        <div>
          <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-bold text-[#1F3D2B]">
            {status === 'delivered' && 'تم اعتماد التسليم ✅'}
            {status === 'closed' && 'المشروع مُغلق'}
            {status === 'cancelled' && 'المشروع مُلغى'}
          </p>
          {project.approved_at && (
            <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#3D512D] mt-0.5">
              اعتمده {project.approved_by} — {new Date(project.approved_at).toLocaleString('ar-SA')}
            </p>
          )}
        </div>
      </section>
    );
  }

  if (isPending) {
    return (
      <section
        data-testid="approval-pending-bar"
        className="mb-5 bg-[#E9DFF3] border border-[#C4B0DA] rounded-2xl px-5 py-4"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-bold text-[#573F7A]">
              بانتظار اعتماد المدير التنفيذي
            </p>
            <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#5C4A7A] mt-0.5">
              رُفع بواسطة {project.approval_requested_by || '—'}
              {project.approval_requested_at &&
                ` — ${new Date(project.approval_requested_at).toLocaleString('ar-SA')}`}
            </p>
          </div>
          {isDirector && (
            <div className="flex gap-2">
              <button
                data-testid="director-approve-btn"
                onClick={() => onDecide(true)}
                className="text-[12.5px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-4 py-2 rounded-lg hover:bg-[#163020]"
              >
                اعتماد
              </button>
              <button
                data-testid="director-reject-btn"
                onClick={() => onDecide(false)}
                className="text-[12.5px] font-['Noto_Kufi_Arabic'] font-semibold border border-[#B84A3A] text-[#B84A3A] px-4 py-2 rounded-lg hover:bg-[#FBEDEA]"
              >
                رفض
              </button>
            </div>
          )}
        </div>
        {project.approval_note && (
          <div className="mt-3 bg-white rounded-lg p-3 text-[12px] font-['Noto_Kufi_Arabic'] text-[#3D512D]">
            <span className="text-[#7A866E]">ملاحظة القائد: </span>
            {project.approval_note}
          </div>
        )}
      </section>
    );
  }

  // Normal in-flight project: show "Request approval" if the user can edit.
  if (!canEdit) return null;

  return (
    <section
      data-testid="approval-request-bar"
      className="mb-5 bg-white border border-[#E6E0D6] rounded-2xl px-5 py-4 flex items-center justify-between flex-wrap gap-3"
    >
      <div>
        <p className="font-['Noto_Kufi_Arabic'] text-[13px] font-bold text-[#1F3D2B]">
          هل انتهيتِ من تنفيذ المشروع؟
        </p>
        <p className="font-['Noto_Kufi_Arabic'] text-[11.5px] text-[#5C6D49] mt-0.5">
          ارفعي طلب اعتماد التسليم للمدير التنفيذي لإغلاق المشروع رسميًا.
        </p>
      </div>
      <button
        data-testid="request-approval-btn"
        onClick={onRequest}
        className="text-[12.5px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-5 py-2 rounded-lg hover:bg-[#163020]"
      >
        طلب اعتماد التسليم
      </button>
    </section>
  );
};
